import useStore from '../../store'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { loginRequest } from '../../configs/msalAuthConfig'
import { useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { tdeBaseUrl } from '../../constants/apiUrl'
import { tdeAuditingEnabled } from '../../utilities/featureFlags'
import rollbarLogger from '../../utilities/rollbarLogger'
import Spinner from '../common/Spinner'

export default function MSALRedirect() {
  const { instance } = useMsal()
  const azureVerifiedAccount = instance.getActiveAccount()
  const loginTasks = useStore((state) => state.loginTasks)
  const setErrMessage = useStore((state) => state.setErrMessage)
  const validateAzureToken = useStore((state) => state.validateAzureToken)
  const clearTdeRequestHeaders = useStore((state) => state.clearTdeRequestHeaders)
  const navigate = useNavigate()

  useEffect(() => {
    if (azureVerifiedAccount) {
      acquireApiTokens()
    }
  }, [azureVerifiedAccount])

  const isInteractionRequired = (error) =>
    error instanceof InteractionRequiredAuthError ||
    error?.errorCode === 'interaction_required' ||
    error?.errorCode === 'login_required' ||
    error?.errorCode === 'consent_required'

  const acquireFreshMicrosoftToken = async () => {
    const activeAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0]
    try {
      return await instance.acquireTokenSilent({ ...loginRequest, account: activeAccount })
    } catch (error) {
      if (isInteractionRequired(error)) {
        return await instance.acquireTokenPopup(loginRequest)
      }
      throw error
    }
  }

  const acquireTDEJwt = async (microsoftToken) => {
    const response = await fetch(`${tdeBaseUrl}/api/v2/omniauth/azure_token_validation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ idToken: microsoftToken.idToken }),
    })

    if (response.status !== 200) {
      throw new Error(
        response.status === 401
          ? 'Microsoft sign-in failed'
          : 'Something went wrong while signing in'
      )
    }

    const { data } = await response.json()
    const appJwt = data?.attributes?.jwt
    if (!appJwt) {
      throw new Error('TDE response did not include a JWT')
    }
    useStore.getState().setTdeRequestHeaders(appJwt)
    return appJwt
  }

  const acquireTDEJwtSafely = async (microsoftToken, dfpData) => {
    try {
      await acquireTDEJwt(microsoftToken)
    } catch (err) {
      console.error(err)
      clearTdeRequestHeaders()
      rollbarLogger(dfpData).logError('Unable to authenticate with TDE during employee login.', err)
      setErrMessage('Unable to login to TDE to retrieve trailer audits')
    }
  }

  const acquireDFPJwt = async (microsoftToken) => {
    const response = await validateAzureToken(microsoftToken.accessToken)
    const data = await response.json()
    if (response.status !== 200) {
      const error = new Error('Unrecognized MS Teams user')
      error.userMessage = 'Unrecognized MS Teams user'
      throw error
    }
    return data
  }

  const acquireApiTokens = async () => {
    try {
      await instance.initialize()
      const microsoftToken = await acquireFreshMicrosoftToken()
      const dfpData = await acquireDFPJwt(microsoftToken)
      if (tdeAuditingEnabled) {
        await acquireTDEJwtSafely(microsoftToken, dfpData)
      }
      loginTasks(dfpData)
    } catch (err) {
      console.error(err)
      setErrMessage(err?.userMessage ?? 'An error occurred while validating user credentials')
    } finally {
      instance.setActiveAccount(null)
      navigate('/')
    }
  }

  return(
    <div
      className="flex justify-center items-center w-full max-h-dvh min-h-dvh"
    >
      <Spinner />
    </div>
  )
}